/*@flow*/
/* eslint-disable class-methods-use-this*/
import * as I from 'immutable';
import getLatLngs from 'lib/getLatLngs';
import mitt from 'lib/mitt';
import { closeTo } from './util';
import { polygonIntersects } from './geom';
import L from 'leaflet';
import '../TouchEvents';
import Tooltip from './tooltip';

export const pablo: {|
  emit: (type: string, evt: any) => void,
  off: (o: any | string, evt: any) => void,
  on: (o: any | string, evt: any) => void,
|} = mitt();

interface IPolygon {
  isValid(): boolean;
  vertices(): Leaflet$Class[];
}

interface IHandler {
  disable(): void;
  enable(): void;
  enabled(): boolean;
}

interface Marker {
  _origLatLng: Leaflet$LatLng;
  _latlng: Leaflet$LatLng;
  setLatLng(Leaflet$LatLng): any;
  getLatLng(): Leaflet$LatLng;
  _prev: Marker;
  _middleLeft: Marker;
  _next: Marker;
  _middleRight: Marker;
  _index: number;
}

const safePreventDefault = (e) => {
  // $FlowFixMe[prop-missing]
  e.originalEvent && L.DomEvent.preventDefault(e.originalEvent);
};

const updatePrevNext = (marker1, marker2) => {
  if (marker1) {
    marker1._next = marker2;
  }
  if (marker2) {
    marker2._prev = marker1;
  }
};

const FILL_COLOR = '#ababab85';

const mkPolygon = (latLngs) =>
  L.polygon(latLngs, {
    stroke: true,
    color: '#EE7B2C',
    weight: 1,
    opacity: 0.5,
    fillOpacity: 0.2,
    fillColor: FILL_COLOR,
    clickable: true,
    className: 'draw-polygon',
  });

const mkMarker = (latlng) => {
  let icon = L.divIcon({
    iconSize: new L.Point(14, 14),
    className: 'leaflet-div-icon leaflet-editing-icon',
    type: 'div',
  });
  // if touch, switch to touch icon
  if (L.Browser.mobile) {
    icon = L.divIcon({
      iconSize: new L.Point(20, 20),
      className: 'leaflet-div-icon leaflet-editing-icon leaflet-touch-icon',
      type: 'div',
    });
  }
  const MyMarker = L.Marker.Touch.extend({
    options: {
      draggable: true,
      icon,
      zIndexOffset: 2001,
    },
    getIcon() {
      return this._icon;
    },
    getPosition() {
      return L.DomUtil.getPosition(this._icon);
    },
    intersect(point: Leaflet$Point) {
      const point1 = this.getPosition();
      return closeTo(point, point1);
    },
  });
  return new MyMarker(latlng);
};

export class DrawHandler implements IHandler, IPolygon {
  /* :: _map: Leaflet$Map; */
  /* :: _geojson: ?Object; */
  /* :: _latlngs: ?Leaflet$LatLng[]; */
  /* :: markers: ?Leaflet$Marker[]; */
  /* :: _enabled: boolean; */
  /* :: isEditing: boolean; */
  /* :: markerGroup: Leaflet$FeatureGroup; */
  /* :: _poly: Leaflet$Polygon; */
  /* :: activeMarker: ?Leaflet$Marker; */
  /* :: mouseDownOrigin: Leaflet$Point; */
  /* :: mouseMarker: Leaflet$Marker; */
  /* :: canDrag: boolean; */
  /* :: tooltip: Tooltip; */
  /* :: originalFillColor: ?string; */
  /* :: preDragOrigLatLng: Leaflet$LatLng; */
  /* :: preDragPolygonLatlngs: Leaflet$LatLng[]; */
  /* :: isDragging: boolean; */

  canDrag: boolean = true;
  _enabled: boolean = false;
  _latlngs: Array<Leaflet$LatLng> = [];
  markers: Array<any> = [];
  isEditing: boolean = false;
  isShapeValid: boolean = true;
  originalFillColor: ?string = null;
  isDragging: boolean = false;

  constructor(map: Leaflet$Map) {
    this._map = map;
    this.tooltip = new Tooltip(map);
  }

  get polygon(): Leaflet$Polygon {
    return this._poly;
  }

  set geojson(value: Object) {
    this._geojson = value;

    if (!this._poly) return;

    let latlngs = [];
    if (value) {
      latlngs = getLatLngs(value);
    }

    this._latlngs = latlngs;

    this._poly.setLatLngs(latlngs);
  }

  set active(bool: boolean) {
    if (bool && !this.enabled()) {
      this.enable();
    } else if (!bool && this.enabled()) {
      this.disable();
    }
  }

  defaultShape(): any {
    return L.LineUtil.isFlat(this._latlngs) ? this._latlngs : this._latlngs[0];
  }

  isValid(): boolean {
    return (
      this._poly &&
      this.isShapeValid &&
      I.fromJS(this._poly.getLatLngs())
        .get(0)
        .filter((v) => v.size > 0).size > 2
    );
  }

  vertices(): Array<Leaflet$Class> {
    return this.markerGroup.getLayers();
  }

  clearActiveMarker() {
    delete this.activeMarker;
  }

  on(name: string, handler: Function) {
    pablo.on(name, handler);
  }

  off(name: string, handler: Function) {
    pablo.off(name, handler);
  }

  onMarkerClick: (e: { target: Leaflet$Marker, ... }) => void = (e: {
    target: Leaflet$Marker,
  }) => {
    this.activeMarker = e.target;
    // NOTE: The original plugin removes the marker when the user clicks on it.
    // Instead, we "activate" the marker. When a marker is in this state, it can
    // be deleted by clicking the "Backspace" button.
  };

  spliceLatLngs(i: number, j: number, replacement: Leaflet$LatLng): any {
    let latlngs = this.defaultShape();
    let removed = latlngs.splice(i, j, replacement);
    this._poly._convertLatLngs(latlngs, true);
    this._poly.redraw();
    return removed;
  }

  removeActiveMarker() {
    const activeMarker = this.activeMarker;
    if (!activeMarker) return;
    this.markerGroup.removeLayer(activeMarker);
    // Remove vertex latlng from this._latlngs
    this._latlngs = (this._latlngs.filter(
      (v) => !v.equals(activeMarker.getLatLng())
    ): Leaflet$LatLng[]);
    this.updatePolygonShape();
    this.updatePolygonFill();
    this.clearActiveMarker();
    this.updateMarkers();
    this.isDragging = false;
  }

  updatePolygonShape() {
    this._poly.setLatLngs(this._latlngs);
  }

  createVertex(latlng: Leaflet$LatLng, index?: number): any {
    const marker = (mkMarker(latlng): any);
    marker._origLatLng = latlng;
    marker._index = index;
    marker
      .on('click', this.onMarkerClick)
      .on('dblclick', this.onMarkerDblClick)
      .on('drag', this.onMarkerDrag)
      .on('dragstart', this.onMarkerDragStart)
      .on('dragend', this.onMarkerDragEnd)
      .on('touchmove', this.onMarkerTouchMove)
      .on('touchstart', this.onMarkerDragStart)
      .on('touchend', this.onMarkerDragEnd)
      .on('MSPointerMove', this.onMarkerTouchMove);
    this.markerGroup.addLayer(marker);
    return marker;
  }

  // Used when drawing a polygon.
  //
  // Determines the index of nearest vertex and
  // splice this index at that location, so that the
  // polygon is well connected.
  addVertex(latlng: Leaflet$LatLng) {
    if (!this.canDrag) return;

    // Add the vertex at the end.
    const index = this.defaultShape().length;
    const poly = this.polygon;
    const marker = this.createVertex(latlng, index);
    this.markers.push(marker);
    this._latlngs.push(latlng);
    this.updatePolygonShape();

    // Show error message if the polygon intersects.
    if (polygonIntersects(poly)) {
      this.originalFillColor = poly.options.fillColor;
      poly.setStyle({ fillColor: 'red' });
      this.updateTooltipErrorText();
      this.isShapeValid = false;
    } else {
      this.isShapeValid = true;
      if (this.originalFillColor) {
        poly.setStyle({ fillColor: this.originalFillColor });
        this.originalFillColor = null;
      }
      pablo.emit('draw:vertex_added', this);
    }
  }

  // mouseMarker is used to follow the location of the mouse
  // capture click events and determine where to drop a new marker.
  createMouseMarker() {
    const icon = L.divIcon({
      className: 'leaflet-mouse-marker leaflet-crosshair',
      iconAnchor: [50, 50],
      iconSize: [100, 100],
    });

    // Make a transparent marker that will used to catch click events.
    this.mouseMarker = L.marker(this._map.getCenter(), {
      icon,
      opacity: 0.5,
      // This should be > than the highest z-index any map layers
      zIndexOffset: 1000,
      outline: 0,
    });

    // Bind events to mouse marker
    this.mouseMarker
      .on('mousemove', this.onMouseMove)
      .on('click', this.onMouseDown)
      .on('mousedown', this.onMouseDown)
      .on('mouseup', this.onMouseUp)
      .on('MSPointerMove', this.onMouseMove)
      .on('MSPointerUp', this.onMouseUp);

    this._map.addLayer(this.mouseMarker);
  }

  // Remove mouse marker
  removeMouseMarker() {
    if (!this.mouseMarker) return;
    this._map.removeLayer(this.mouseMarker);
    this.mouseMarker.clearAllEventListeners();
  }

  initMarkers() {
    let latlngs = this.defaultShape();
    this.markers = [];

    latlngs.forEach((latlng, i) => {
      let marker = this.createVertex(latlng, i);
      this.markers.push(marker);
    });

    if (this.isEditing) {
      this.initMiddleMarkers();
    }
  }

  initMiddleMarkers() {
    let markerLeft;
    let markerRight;

    let i = 1;
    let j = 0;
    while (i < this.defaultShape().length) {
      if (L.Polygon && this._poly instanceof L.Polygon) {
        markerLeft = this.markers[j];
        markerRight = this.markers[i];

        this.createMiddleMarker(markerLeft, markerRight);
        updatePrevNext(markerLeft, markerRight);
      }
      j = i++;
    }
  }

  // Clear markers and update their location
  updateMarkers() {
    this.markerGroup.clearLayers();
    this.initMarkers();
  }

  createMiddleMarker(marker1: Marker, marker2: Marker) {
    const latlng = this.getMiddleLatLng(marker1, marker2);
    const marker = this.createVertex(latlng);

    marker.setOpacity(0.6);

    marker1._middleRight = marker2._middleLeft = marker;

    const onDragStart = () => {
      marker.off('touchmove', onDragStart);
      let i = marker2._index;

      marker._index = i;

      // When this is clicked on, after being dragged, add it
      // to the markerGroup.
      marker.off('click', onClick).on('click', this.onMarkerClick);

      latlng.lat = marker.getLatLng().lat;
      latlng.lng = marker.getLatLng().lng;
      this.spliceLatLngs(i, 0, latlng);
      this.markers.splice(i, 0, marker);

      marker.setOpacity(1);

      this.updateIndexes(i, 1);
      marker2._index++;
      updatePrevNext(marker1, marker);
      updatePrevNext(marker, marker2);
    };

    const onDragEnd = () => {
      marker.off('dragstart', onDragStart);
      marker.off('dragend', onDragEnd);
      marker.off('touchmove', onDragStart);

      this.createMiddleMarker(marker1, marker);
      this.createMiddleMarker(marker, marker2);
    };

    const onClick = () => {
      onDragStart();
      onDragEnd();
    };

    const onDrag = (e) => {
      if (!this.canDrag) {
        e.originalEvent.preventDefault();
      }
    };

    marker
      .on('click', onClick)
      .on('drag', onDrag)
      .on('dragstart', onDragStart)
      .on('dragend', onDragEnd)
      .on('touchmove', onDragStart);

    this.markerGroup.addLayer(marker);
  }

  getMiddleLatLng(marker1: Marker, marker2: Marker): Leaflet$LatLng {
    let map = this._poly._map,
      p1 = map.project(marker1.getLatLng()),
      p2 = map.project(marker2.getLatLng());

    // $FlowFixMe[prop-missing]
    return map.unproject(p1._add(p2)._divideBy(2));
  }

  updateIndexes(index: number, delta: number) {
    this.markerGroup.eachLayer((marker) => {
      // $FlowFixMe[prop-missing]
      if (marker._index > index) {
        // $FlowFixMe[prop-missing]
        marker._index += delta;
      }
    });
  }

  updateTooltipHintText() {
    if (this.isEditing) {
      this.tooltip.updateContent({
        text: 'Click on a marker to drag it.',
        subtext: 'Double click a marker to remove it.',
      });
    } else {
      this.tooltip.updateContent({
        text: 'Click anywhere to add a new point.',
      });
    }
  }

  updateTooltipErrorText() {
    // Update tooltip style
    this.tooltip.showAsError();
    this.tooltip.updateContent({
      text: 'Error: You cannot cross lines.',
      subtext: 'Please correct your shape',
    });
  }

  enable() {
    if (this.enabled()) return;
    L.DomUtil.disableTextSelection();

    this._enabled = true;
    this.isEditing = Boolean(this._geojson);
    this.isShapeValid = true;

    // Enable tooltip
    this.tooltip.enable();

    this.updateTooltipHintText();

    this._latlngs = this._geojson ? getLatLngs(this._geojson) : [];

    if (this._poly) {
      this._map.removeLayer(this._poly);
    }
    this._poly = mkPolygon(this._latlngs);
    this._map.addLayer(this._poly);

    // Listen for keydown events
    $(window).keydown(this.onKeyDown);

    // Draw a polygon
    if (!this.markerGroup) {
      this.markerGroup = L.featureGroup([]);
    }

    // Create vertices
    this.initMarkers();

    // Add event listeners to add vertex markers
    this._map
      .on('click', this.onTouch)
      .on('mousemove', this.onMouseMove)
      .on('mousedown', this.onMouseDown)
      .on('dragstart', this.onMapDragStart)
      .on('dragend', this.onMapDragEnd)
      .on('MSPointerMove', this.onMouseMove)
      .on('MSPointerUp', this.onMouseUp);

    this.createMouseMarker();

    this._map.addLayer(this.markerGroup);
  }

  disable() {
    if (!this.enabled()) {
      return;
    }
    this._enabled = false;
    L.DomUtil.enableTextSelection();

    this.tooltip.disable();

    // Remove bindings for keyboard events.
    $(window).off('keydown', this.onKeyDown);

    // Remove mouse marker
    this._map
      .off('mousedown', this.onMouseDown)
      .off('mouseup', this.onMouseUp)
      .off('click', this.onTouch)
      .off('mousemove', this.onMouseMove)
      .off('dragstart', this.onMapDragStart)
      .off('dragend', this.onMapDragEnd)
      .on('MSPointerMove', this.onMouseMove)
      .on('MSPointerUp', this.onMouseUp);

    this.removeMouseMarker();

    // Unbind events from vertex layers
    this.markerGroup.getLayers().forEach((layer) => {
      // $FlowFixMe[prop-missing]
      layer.clearAllEventListeners();
    });

    // Remove vertex layers
    this._map.removeLayer(this.markerGroup);

    // Remove polygon
    this._poly.setLatLngs([]);

    // Clean up instance properties
    this.markerGroup = L.featureGroup([]);
    this.markers = [];
    this._latlngs = [];
    this.geojson = null;
  }

  enabled(): boolean {
    return this._enabled;
  }

  enableDrag() {
    this.canDrag = true;
    $('.leaflet-mouse-marker')
      .addClass('leaflet-crosshair')
      .removeClass('leaflet-no-drop');
  }

  disableDrag() {
    this.canDrag = false;
    $('.leaflet-mouse-marker')
      .removeClass('leaflet-crosshair')
      .addClass('leaflet-no-drop');
  }

  onMarkerDragEnd: (e: { target: Marker, ... }) => void = (e: {
    target: Marker,
  }) => {
    let marker = e.target;
    let poly = this._poly;
    if (!this.canDrag) return;

    this.isDragging = false;
    setTimeout(() => {
      this.isDragging = false;
    }, 30);

    // If the polygon intersects
    if (polygonIntersects(poly)) {
      this.isShapeValid = false;
    } else {
      // Remove validation error
      this.isShapeValid = true;

      this.updateTooltipHintText();

      // Return polygon to its original color
      poly.setStyle({ fillColor: FILL_COLOR });
      this.originalFillColor = null;

      // The polygon does not intersect with the new location
      // of the marker. Update the polygon.
      if (marker._middleLeft) {
        const mid = this.getMiddleLatLng(marker._prev, marker);
        marker._middleLeft.setLatLng(mid);
      }

      if (marker._middleRight) {
        const mid = this.getMiddleLatLng(marker, marker._next);
        marker._middleRight.setLatLng(mid);
      }

      // Refresh the bounds when draging
      poly._bounds._southWest = L.latLng(Infinity, Infinity);
      poly._bounds._northEast = L.latLng(-Infinity, -Infinity);
      let latlngs = poly.getLatLngs();
      poly._convertLatLngs(latlngs, true);
      poly.redraw();
      pablo.emit('draw:drag', this);
    }
  };

  onMarkerDragStart: (e: { target: Marker, ... }) => void = (e: {
    target: Marker,
  }) => {
    let marker = e.target;
    if (!marker._origLatLng) return;
    if (!this.canDrag) return;
    this.isDragging = true;
  };

  onMarkerDrag: (e: { target: Marker, ... }) => void = (e: {
    target: Marker,
  }) => {
    let marker = e.target;
    if (!marker._origLatLng) return;
    L.Util.extend(marker._origLatLng, marker._latlng);
    this.updatePolygonShape();
    this.updatePolygonFill();
  };

  updatePolygonFill() {
    let poly = this._poly;
    // If the polygon intersects
    if (polygonIntersects(poly)) {
      // Paint the polygon red.
      poly.setStyle({ fillColor: 'red' });
      // Update error message
      this.updateTooltipErrorText();
    } else {
      // Return polygon to its original color
      poly.setStyle({ fillColor: FILL_COLOR });
      // Update tooltip style
      this.tooltip.removeError();
      // Update error message
      this.updateTooltipHintText();
    }
  }

  onMarkerTouchMove: (e: {
    originalEvent: TouchEvent,
    target: Marker,
  }) => void = (e: { originalEvent: TouchEvent, target: Marker }) => {
    const touch = e.originalEvent.touches[0];
    const layerPoint = this._map.mouseEventToLayerPoint(touch);
    const latlng = this._map.layerPointToLatLng(layerPoint);
    const marker = e.target;

    L.extend(marker._origLatLng, latlng);

    if (marker._middleLeft) {
      marker._middleLeft.setLatLng(this.getMiddleLatLng(marker._prev, marker));
    }
    if (marker._middleRight) {
      marker._middleRight.setLatLng(this.getMiddleLatLng(marker, marker._next));
    }

    this.updatePolygonShape();
    this.updateMarkers();
    this.updatePolygonFill();
  };

  onMouseMove: (e: Leaflet$MouseEvent) => void = (e: Leaflet$MouseEvent) => {
    let newPos = this._map.mouseEventToLayerPoint(e.originalEvent);
    let latlng = this._map.layerPointToLatLng(newPos);
    this.mouseMarker.setLatLng(latlng);
    this.tooltip.updatePosition(latlng);
    safePreventDefault(e.originalEvent);
  };

  onKeyDown: (e: JQueryEventObject) => void = (e: JQueryEventObject) => {
    if (e.key === 'Backspace') {
      this.removeActiveMarker();
    }
  };

  onMarkerDblClick: (e: { target: Leaflet$Marker, ... }) => void = (e: {
    target: Leaflet$Marker,
  }) => {
    this.activeMarker = e.target;
    this.removeActiveMarker();
  };

  onTouch: (e: Leaflet$MouseEvent) => void = (e: Leaflet$MouseEvent) => {
    // #TODO: use touchstart and touchend vs using click(touch start & end).
    if (L.Browser.mobile) {
      // #TODO: get rid of this once leaflet fixes their click/touch.
      this.onMouseDown(e);
      this.onMouseUp(e);
    }
  };

  onMouseDown: (e: Leaflet$MouseEvent) => void = (e: Leaflet$MouseEvent) => {
    let o = e.originalEvent;
    this.mouseDownOrigin = L.point(o.clientX, o.clientY);
    safePreventDefault(e.originalEvent);
  };

  onMapDragStart: () => void = () => {
    this.isDragging = true;
  };

  onMapDragEnd: () => void = () => {
    setTimeout(() => {
      this.isDragging = false;
    }, 30);
  };

  onMouseUp: (e: Leaflet$MouseEvent) => void = (e: Leaflet$MouseEvent) => {
    safePreventDefault(e.originalEvent);
    if (!(e.latlng.lat && e.latlng.lng)) return;

    const doesIntersect = (layer) => {
      // $FlowFixMe[prop-missing]
      return e.layerPoint && layer.intersect(e.layerPoint);
    };
    // Find the vertex at this cooordinate.
    const vertex = this.markerGroup.getLayers().find(doesIntersect);

    // When the user clicks on a vertex, we activate that vertex, which
    // created mid-points between the nearest two vertices and the clicked
    // index.
    if (vertex) {
      this.onMarkerClick({ target: ((vertex: any): Leaflet$Marker) });
      // When editing the polygon, we cannot add new vertices randomly.
      // Instead, we should click on an existing vertex and manipulate the
      // mid-points between vertices.
    } else if (this.mouseDownOrigin && !this.isEditing && !this.isDragging) {
      this.clearActiveMarker();
      // Add vertex to polygon
      this.addVertex(e.latlng);
    }
  };
}

export default (owner: Leaflet$Map): DrawHandler => new DrawHandler(owner);
